import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import '../../shared/ui/ag-grid-theme.css'
import type {
  ColDef,
  ColumnResizedEvent,
  DragStoppedEvent,
  FilterChangedEvent,
  RowSelectionOptions,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import {
  canEditCustoms,
  type CustomsAnswerPatchDto,
  type CustomsQueueResponse,
  type CustomsQueueRow,
} from '@crm/shared'
import { patchCustomsAnswer } from '../../shared/api/customs.api'
import { apiErrorText } from '../../shared/api/errorText'
import { useAuthStore } from '../auth/authStore'
import { useOrderColState } from '../orders/hooks/useOrderColState'
import { AG_GRID_LOCALE_RU } from '../../shared/ag-grid/locale'
import { useGridSelection } from '../../shared/hooks/useGridSelection'
import { PageLoader } from '../../shared/ui/Spinner'
import { EmptyState } from '../../shared/ui/EmptyState'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { toast } from '../../shared/ui/toastStore'
import { useCustomsQueue } from './useCustomsQueue'
import { visibleSelectedIds } from './customsSelection'
import {
  buildCustomsColDefs,
  customsDefaultColDef,
  toCustomsGridRow,
  type CustomsGridRow,
} from './customsColDefs'
import { CustomsToolbar } from './components/CustomsToolbar'
import { CustomsActionsBar, bulkResultText } from './components/CustomsActionsBar'
import { CustomsAnswerModal } from './components/CustomsAnswerModal'
import { CustomsStatusModal } from './components/CustomsStatusModal'
import { CustomsCommentsDrawer } from './components/CustomsCommentsDrawer'

ModuleRegistry.registerModules([AllCommunityModule])

// Строки с переносом текста выше 40px — флажок выравниваем по центру строки.
const SELECTION_COLUMN = { cellClass: 'flex items-center' }

/**
 * «Нетарифка» (логист, Л1–Л3, docs/features/nontariff.md): очередь позиций из-за
 * рубежа — Китай, Производители, Стоки. Логист вводит ответ брокера, ставит
 * статусы, отправляет партномера брокеру и переписывается с менеджером в
 * комментариях позиции. ADMIN, ГД и РОЗ — только просмотр. Цен и квот здесь нет.
 */
export function CustomsQueuePage() {
  const user = useAuthStore((s) => s.user)
  const canEdit = canEditCustoms(user?.role)
  const qc = useQueryClient()
  const q = useCustomsQueue()
  const [searchParams, setSearchParams] = useSearchParams()

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [answerRows, setAnswerRows] = useState<CustomsQueueRow[] | null>(null)
  const [needsInfoIds, setNeedsInfoIds] = useState<string[] | null>(null)
  const [comments, setComments] = useState<{ itemId: string; row?: CustomsQueueRow } | null>(null)

  // Переход из уведомления: /customs?itemId=<id>&comments=1 — сразу панель комментариев.
  const linkItemId = searchParams.get('itemId')
  const linkComments = searchParams.get('comments') === '1'
  useEffect(() => {
    if (linkItemId && linkComments) setComments({ itemId: linkItemId })
  }, [linkItemId, linkComments])

  const closeComments = useCallback(() => {
    setComments(null)
    if (searchParams.has('itemId') || searchParams.has('comments')) {
      const next = new URLSearchParams(searchParams)
      next.delete('itemId')
      next.delete('comments')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const rowData = useMemo(() => q.rows.map(toCustomsGridRow), [q.rows])
  const selectedRows = useMemo(() => {
    const ids = new Set(selectedIds)
    return q.rows.filter((r) => ids.has(r.itemId))
  }, [q.rows, selectedIds])
  // Пустой список — грид размонтирован вместе с выделением: сбрасываем и его копию.
  useEffect(() => {
    if (rowData.length === 0) setSelectedIds([])
  }, [rowData.length])

  const gridRef = useRef<AgGridReact<CustomsGridRow>>(null)
  const { onCellMouseDown, onCellMouseOver, getCellStyle } = useGridSelection(gridRef)
  const { saveState, onGridReady } = useOrderColState('crm_customs_cols_v1', user?.id)

  const openComments = useCallback(
    (row: CustomsQueueRow) => setComments({ itemId: row.itemId, row }),
    [],
  )
  // Правка ячейки ответа: строка обновляется из ответа сервера прямо в загруженных
  // страницах — очередь не перечитывается целиком, строка не «уезжает» во время
  // работы (во вкладку «Обработано» она перейдёт при следующем обновлении).
  const editAnswer = useCallback(
    async (row: CustomsQueueRow, patch: CustomsAnswerPatchDto) => {
      try {
        const updated = await patchCustomsAnswer(row.itemId, patch)
        qc.setQueriesData<InfiniteData<CustomsQueueResponse>>(
          { queryKey: ['customs', 'queue'] },
          (data) =>
            data && {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                items: page.items.map((it) => (it.itemId === updated.itemId ? updated : it)),
              })),
            },
        )
        void qc.invalidateQueries({ queryKey: ['customs', 'item', row.itemId] })
      } catch (e) {
        // Партномер — всегда: при нескольких правках подряд видно, какая не сохранилась.
        toast.error(`${row.partNumber}: ${apiErrorText(e, 'не удалось сохранить')}`)
      }
    },
    [qc],
  )
  const colDefs = useMemo(
    () =>
      buildCustomsColDefs({
        canEdit,
        onOpenComments: openComments,
        onEditAnswer: (row, patch) => void editAnswer(row, patch),
      }),
    [canEdit, openComments, editAnswer],
  )
  // Чекбоксы строк — только логисту (массовые действия); объект стабильный,
  // чтобы грид не перенастраивал выделение на каждом рендере страницы.
  // Галочка в шапке отмечает только строки, прошедшие фильтры колонок.
  const rowSelection = useMemo<RowSelectionOptions | undefined>(
    () =>
      canEdit
        ? {
            mode: 'multiRow',
            enableClickSelection: false,
            checkboxes: true,
            headerCheckbox: true,
            selectAll: 'filtered',
          }
        : undefined,
    [canEdit],
  )
  // Фильтр колонки спрятал отмеченные строки — снимаем с них отметку, чтобы
  // действие не задело невидимые позиции.
  const onFilterChanged = useCallback((e: FilterChangedEvent<CustomsGridRow>) => {
    const hidden = e.api.getSelectedNodes().filter((n) => !n.displayed)
    if (hidden.length > 0) e.api.setNodesSelected({ nodes: hidden, newValue: false })
    setSelectedIds(visibleSelectedIds(e.api))
  }, [])
  const defaultColDef = useMemo<ColDef>(
    () => ({ ...customsDefaultColDef, cellStyle: getCellStyle }),
    [getCellStyle],
  )

  const clearSelection = useCallback(() => {
    const api = gridRef.current?.api
    if (api && !api.isDestroyed()) api.deselectAll()
    setSelectedIds([])
  }, [])

  const afterBulk = useCallback(
    (prefix: string, res: { updated: number; skipped: number }) => {
      toast.success(bulkResultText(prefix, res))
      clearSelection()
      void qc.invalidateQueries({ queryKey: ['customs'] })
    },
    [clearSelection, qc],
  )

  const { queue, counts, total } = q
  const loadedText =
    queue.data && total > 0
      ? `всего: ${total.toLocaleString('ru-RU')}` +
        (q.rows.length < total ? ` · загружено: ${q.rows.length.toLocaleString('ru-RU')}` : '')
      : ''

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="text-xl font-semibold text-gray-900">Нетарифка</h1>
            {loadedText && <span className="text-sm text-gray-500">{loadedText}</span>}
            {queue.isFetching && !queue.isLoading && !queue.isFetchingNextPage && (
              <span className="text-xs text-gray-400">обновляю…</span>
            )}
          </div>
          {!canEdit && (
            <span className="text-xs text-gray-500 bg-gray-100 rounded-lg px-2.5 py-1">
              Просмотр: правка доступна логисту
            </span>
          )}
        </div>

        <CustomsToolbar
          filters={q.filters}
          onChange={q.updateFilters}
          onReset={q.resetFilters}
          hasActiveFilters={q.hasActiveFilters}
          searchInput={q.searchInput}
          onSearchInput={q.setSearchInput}
          counts={counts}
          managers={q.managers}
        />

        {canEdit && (
          <CustomsActionsBar
            selected={selectedRows}
            onAnswer={() => setAnswerRows(selectedRows)}
            onNeedsInfo={() => setNeedsInfoIds(selectedRows.map((r) => r.itemId))}
            onDone={clearSelection}
          />
        )}

        {queue.isLoading ? (
          <PageLoader label="Загружаю очередь нетарифки…" />
        ) : queue.isError ? (
          <ErrorMessage error={queue.error} />
        ) : rowData.length === 0 ? (
          <EmptyState
            title="Позиций нет"
            description={
              q.hasActiveFilters
                ? 'Под фильтры ничего не подошло — сбросьте их.'
                : 'Сюда попадают позиции, которые менеджер отправил кнопкой «Отправить брокеру» в карточке заявки.'
            }
          />
        ) : (
          <>
            <div className="crm-table ag-theme-alpine rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm h-[calc(100vh-320px)] min-h-[400px]">
              <AgGridReact<CustomsGridRow>
                ref={gridRef}
                rowData={rowData}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                localeText={AG_GRID_LOCALE_RU}
                getRowId={(p) => p.data.id}
                onGridReady={onGridReady}
                onCellMouseDown={onCellMouseDown}
                onCellMouseOver={onCellMouseOver}
                onDragStopped={(e: DragStoppedEvent) => saveState(e.api)}
                onColumnResized={(e: ColumnResizedEvent) => {
                  if (e.finished) saveState(e.api)
                }}
                onSortChanged={(e: SortChangedEvent) => saveState(e.api)}
                onViewportChanged={q.onViewportChanged}
                rowSelection={rowSelection}
                selectionColumnDef={SELECTION_COLUMN}
                onSelectionChanged={(e: SelectionChangedEvent<CustomsGridRow>) =>
                  setSelectedIds(visibleSelectedIds(e.api))
                }
                onFilterChanged={onFilterChanged}
                rowHeight={40}
                headerHeight={40}
                suppressDragLeaveHidesColumns
                // Логист правит ячейки двойным кликом — фокус ячеек ему нужен.
                suppressCellFocus={!canEdit}
                stopEditingWhenCellsLoseFocus
                overlayNoRowsTemplate='<span class="text-sm text-gray-400">Нет позиций</span>'
              />
            </div>
            {queue.isFetchingNextPage && (
              <p className="text-center text-sm text-gray-500">Загружаю ещё…</p>
            )}
          </>
        )}
      </div>
      {/* Окна — вне space-y-4: иначе fixed-оверлей получает отступ сверху. */}
      {answerRows && (
        <CustomsAnswerModal
          rows={answerRows}
          onClose={() => setAnswerRows(null)}
          onSaved={(res) => {
            setAnswerRows(null)
            afterBulk('Ответ записан', res)
          }}
        />
      )}
      {needsInfoIds && (
        <CustomsStatusModal
          itemIds={needsInfoIds}
          onClose={() => setNeedsInfoIds(null)}
          onSaved={(res) => {
            setNeedsInfoIds(null)
            afterBulk('«Нужна информация» отправлено', res)
          }}
        />
      )}
      {comments && (
        <CustomsCommentsDrawer
          key={comments.itemId}
          itemId={comments.itemId}
          row={comments.row}
          onClose={closeComments}
        />
      )}
    </>
  )
}
